import { useState, useEffect, useRef } from 'react';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, X, Maximize2, Minimize2, Send, AlertCircle,
  UserCircle2, Mail, PlusCircle, MessageSquare,
} from 'lucide-react';
import { supabase } from '@/integrations/backend/client';
import { usePublicChat } from '@/hooks/usePublicChat';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { PageSettings } from '@/types/editor';
import { resolveButtonColors, getGlobalButtonStyle } from '@/lib/linkStyle';
import { cn } from '@/lib/utils';
import { trackChatWidgetEvent } from '@/lib/analytics';
import { trackFunnelStep } from '@/lib/funnelTracking';

// ── Types ──────────────────────────────────────────────────────────────────────
interface AgentInfo {
  agent_name: string;
  welcome_message: string;
  require_visitor_info: boolean;
  inactivity_timeout_minutes: number;
  widget_icon: string | null;
  widget_icon_url: string | null;
  widget_position: string | null;
  widget_size: string | null;
  widget_color: string | null;
  widget_text_color: string | null;
  avatar_url: string | null;
}

export interface ChatWidgetProps {
  agentId: string;
  /** UUID of the page owner — used for funnel event tracking */
  pageOwnerId?: string;
  /** Page theme from parent — used to colour the widget to match the host page */
  settings?: PageSettings | null;
}

// ── Component ──────────────────────────────────────────────────────────────────
export function ChatWidget({ agentId, pageOwnerId, settings }: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [agentValid, setAgentValid] = useState<boolean | null>(null);

  // Pre-chat form
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [chatStarted, setChatStarted] = useState(false);

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch agent info on mount (lightweight — determines whether trigger shows)
  useEffect(() => {
    (supabase.from('chat_agents')
      .select('agent_name, welcome_message, is_enabled, require_visitor_info, inactivity_timeout_minutes, widget_icon, widget_icon_url, widget_position, widget_size, widget_color, widget_text_color, avatar_url')
      .eq('id', agentId)
      .eq('is_enabled', true)
      .maybeSingle() as any)
      .then(({ data }: any) => {
        if (!data) { setAgentValid(false); return; }
        setAgentInfo({
          agent_name: data.agent_name || 'AI Assistant',
          welcome_message: data.welcome_message || '',
          require_visitor_info: data.require_visitor_info ?? false,
          inactivity_timeout_minutes: data.inactivity_timeout_minutes ?? 30,
          widget_icon: data.widget_icon || null,
          widget_icon_url: data.widget_icon_url || null,
          widget_position: data.widget_position || 'bottom-right',
          widget_size: data.widget_size || 'medium',
          widget_color: data.widget_color || null,
          widget_text_color: data.widget_text_color || null,
          avatar_url: data.avatar_url || null,
        });
        setAgentValid(true);
        trackChatWidgetEvent('rendered', { agent_id: agentId, source: 'widget' });
      });
  }, [agentId]);

  // Chat hook — only active when panel is open and agent is valid
  const { messages, isLoading, error, isLocked, sendMessage, startNewConversation } = usePublicChat(
    agentValid && isOpen ? agentId : null,
    {
      visitorName: chatStarted ? visitorName : undefined,
      visitorEmail: chatStarted ? visitorEmail : undefined,
      inactivityTimeoutMinutes: agentInfo?.inactivity_timeout_minutes ?? 30,
    },
  );

  // Auto-scroll on new messages / open
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
    return () => clearTimeout(t);
  }, [messages, isOpen]);

  // ── Theme helpers (mirrors PublicChat) ────────────────────────────────────
  const { btnBg: themeBtnBg, btnText: themeBtnText } = settings
    ? resolveButtonColors(settings)
    : { btnBg: 'hsl(var(--primary))', btnText: 'hsl(var(--primary-foreground))' };

  // Widget-specific colors override theme colors for the trigger button
  const widgetBtnBg = agentInfo?.widget_color || themeBtnBg;
  const widgetBtnText = agentInfo?.widget_text_color || themeBtnText;

  // Keep original for panel elements (chat bubbles, send button, etc.)
  const btnBg = themeBtnBg;
  const btnText = themeBtnText;

  // Luminance-based glass — same fix as PublicChat/PublicContact
  // Avoids white-on-white when background_color is light but text_color is also light
  const textColorIsLight = (() => {
    const hex = settings?.text_color?.replace('#', '') ?? '000000';
    if (hex.length < 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
  })();
  // Panel body: higher opacity for readability; header/footer: lighter overlay
  const glassBgPanel   = settings ? (textColorIsLight ? 'rgba(0,0,0,0.80)'   : 'rgba(255,255,255,0.92)') : undefined;
  const glassBgOverlay = settings ? (textColorIsLight ? 'rgba(0,0,0,0.55)'   : 'rgba(255,255,255,0.72)') : undefined;

  const glassBase: React.CSSProperties = settings
    ? {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        backgroundColor: glassBgOverlay,
        color: settings.text_color,
      }
    : {};

  const panelBg: React.CSSProperties = settings
    ? { backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', backgroundColor: glassBgPanel, color: settings.text_color, border: `1px solid ${settings.text_color}1F` }
    : { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' };

  const headerStyle: React.CSSProperties = settings
    ? { ...glassBase, borderBottom: `1px solid ${settings.text_color}1F` }
    : { borderBottom: '1px solid hsl(var(--border))' };

  const footerStyle: React.CSSProperties = settings
    ? { ...glassBase, borderTop: `1px solid ${settings.text_color}1F` }
    : { borderTop: '1px solid hsl(var(--border))' };

  const assistantBubble: React.CSSProperties = settings
    ? { background: textColorIsLight ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.07)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', color: settings.text_color }
    : {};

  const userBubbleStyle = settings ? getGlobalButtonStyle(settings) : undefined;
  const sendBtnStyle = settings ? { background: btnBg, color: btnText, border: 'none' } : undefined;
  const textStyle: React.CSSProperties = settings ? { color: settings.text_color } : {};
  const inputStyle: React.CSSProperties = settings
    ? { color: settings.text_color, background: textColorIsLight ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)', borderColor: `${settings.text_color}30`, caretColor: settings.text_color }
    : {};

  // ── Icon helpers ──────────────────────────────────────────────────────────
  const [agentAvatarError, setAgentAvatarError] = useState(false);
  const [widgetIconError, setWidgetIconError] = useState(false);
  useEffect(() => { setAgentAvatarError(false); setWidgetIconError(false); }, [agentInfo?.avatar_url, agentInfo?.widget_icon_url]);

  const AgentAvatar = () =>
    agentInfo?.avatar_url && !agentAvatarError ? (
      <img src={agentInfo.avatar_url} alt={agentInfo.agent_name} className="w-full h-full object-cover rounded-full" onError={() => setAgentAvatarError(true)} />
    ) : (
      <Bot className="h-4 w-4" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
    );

  // ── Widget positioning and size (from agent settings) ─────────────────────
  const widgetPosition = (agentInfo?.widget_position || 'bottom-right') as
    | 'bottom-right'
    | 'bottom-left'
    | 'top-right'
    | 'top-left';
  const widgetSize = (agentInfo?.widget_size || 'medium') as 'small' | 'medium' | 'large';
  const btnPx = widgetSize === 'small' ? 40 : widgetSize === 'large' ? 64 : 56;
  const iconScale = widgetSize === 'small' ? 0.7 : widgetSize === 'large' ? 1.15 : 1;

  const isTop = widgetPosition.startsWith('top');
  const isLeft = widgetPosition.endsWith('left');
  const panelMotionY = isTop ? -14 : 14;

  const anchorClass = cn(
    'fixed z-[199]',
    isTop ? 'top-4 sm:top-6' : 'bottom-4 sm:bottom-6',
    isLeft ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
  );

  // ── Panel positioning ─────────────────────────────────────────────────────
  const panelClass = isExpanded
    ? 'fixed inset-4 sm:inset-8 z-[200] flex flex-col overflow-hidden rounded-2xl shadow-2xl'
    : cn(
        'fixed z-[200] flex flex-col overflow-hidden rounded-2xl shadow-2xl w-[calc(100vw-2rem)] sm:w-[380px] h-[calc(100vh-108px)] sm:h-[540px]',
        isTop ? 'top-[92px] sm:top-[96px]' : 'bottom-[92px] sm:bottom-[96px]',
        isLeft ? 'left-4 sm:left-6' : 'right-4 sm:right-6',
      );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSend = () => {
    if (!input.trim() || isLoading || isLocked) return;
    // First message without pre-chat form = session start
    if (messages.length === 0 && !(agentInfo?.require_visitor_info)) {
      trackChatWidgetEvent('session_started', {
        agent_id: agentId,
        source: 'widget',
        has_visitor_info: false,
      });
      if (pageOwnerId) {
        trackFunnelStep(pageOwnerId, 'chat', 'chat_message_sent').catch(() => {});
      }
    }
    trackChatWidgetEvent('message_sent', {
      agent_id: agentId,
      source: 'widget',
      message_count: messages.length + 1,
    });
    sendMessage(input);
    setInput('');
  };

  const handleStartChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitorName.trim()) return;
    setChatStarted(true);
    trackChatWidgetEvent('session_started', {
      agent_id: agentId,
      source: 'widget',
      has_visitor_info: true,
    });
  };

  const handleClose = () => {
    trackChatWidgetEvent('closed', { agent_id: agentId, source: 'widget', message_count: messages.length });
    setIsOpen(false);
    setIsExpanded(false);
  };

  // Don't render if agent is confirmed invalid
  if (agentValid === false) return null;

  return (
    <div className={anchorClass}>

      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isOpen && (
            <motion.div
              key="widget-panel"
              initial={{ opacity: 0, y: panelMotionY, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: panelMotionY, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className={panelClass}
            style={panelBg}
          >
            {/* Header */}
            <div className="px-4 py-3.5 flex items-center gap-3 shrink-0" style={headerStyle}>
              <div
                className="h-9 w-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                style={settings ? { background: `${btnBg}33` } : { background: 'hsl(var(--primary)/0.1)' }}
              >
                <AgentAvatar />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm tracking-[-0.01em] truncate" style={textStyle}>
                  {agentInfo?.agent_name ?? '…'}
                </p>
                <p className="text-xs opacity-60" style={textStyle}>
                  {!agentInfo ? 'Carregando…' : isLoading ? 'Digitando…' : isLocked ? 'Conversa encerrada' : 'Online'}
                </p>
              </div>
              <div className="flex items-center gap-0.5">
                {/* Expand / restore toggle */}
                <button
                  onClick={() => {
                    const next = !isExpanded;
                    setIsExpanded(next);
                    trackChatWidgetEvent(next ? 'expanded' : 'minimized', { agent_id: agentId, source: 'widget' });
                  }}
                  title={isExpanded ? 'Reduzir' : 'Expandir'}
                  className={`${isExpanded ? 'flex' : 'hidden sm:flex'} h-7 w-7 rounded-full items-center justify-center opacity-50 hover:opacity-100 transition-opacity`}
                  style={textStyle}
                >
                  {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={handleClose}
                  className="h-7 w-7 rounded-full flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                  style={textStyle}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Loading state */}
            {!agentInfo && (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex gap-1">
                  {[0, 150, 300].map(d => (
                    <span
                      key={d}
                      className="h-2 w-2 rounded-full animate-bounce motion-reduce:animate-none"
                      style={{ background: settings ? `${settings.text_color}99` : 'hsl(var(--muted-foreground)/0.5)', animationDelay: `${d}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pre-chat form */}
            {agentInfo && agentInfo.require_visitor_info && !chatStarted && (
              <form onSubmit={handleStartChat} className="flex-1 overflow-y-auto px-4 py-5 space-y-4 flex flex-col justify-center">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium opacity-80" style={textStyle} htmlFor="wgt-name">
                    Nome <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <UserCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-40" style={textStyle} />
                    <input
                      id="wgt-name"
                      type="text"
                      required
                      value={visitorName}
                      onChange={e => setVisitorName(e.target.value)}
                      placeholder="Seu nome"
                      maxLength={80}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-1 placeholder:opacity-40"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium opacity-80" style={textStyle} htmlFor="wgt-email">
                    E-mail <span className="opacity-50 text-[0.7rem]">(opcional)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-40" style={textStyle} />
                    <input
                      id="wgt-email"
                      type="email"
                      value={visitorEmail}
                      onChange={e => setVisitorEmail(e.target.value)}
                      placeholder="seu@email.com"
                      maxLength={120}
                      className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-1 placeholder:opacity-40"
                      style={inputStyle}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={!visitorName.trim()}
                  className="w-full py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                  style={settings
                    ? { background: btnBg, color: btnText }
                    : { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}
                >
                  Iniciar conversa
                </button>
              </form>
            )}

            {/* Main chat */}
            {agentInfo && (!agentInfo.require_visitor_info || chatStarted) && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
                  {/* Welcome message */}
                  {agentInfo.welcome_message && messages.length === 0 && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2">
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 overflow-hidden"
                        style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary)/0.1)' }}
                      >
                        <AgentAvatar />
                      </div>
                      <div
                        className="max-w-[85%] rounded-2xl rounded-tl-sm px-3.5 py-3"
                        style={settings ? assistantBubble : { background: 'hsl(var(--muted))' }}
                      >
                        <MarkdownRenderer variant="chat" className="text-inherit">
                          {agentInfo.welcome_message}
                        </MarkdownRenderer>
                      </div>
                    </motion.div>
                  )}

                  <AnimatePresence initial={false}>
                    {messages.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18 }}
                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}
                      >
                        {msg.role === 'assistant' && (
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 overflow-hidden"
                            style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary)/0.1)' }}
                          >
                            <AgentAvatar />
                          </div>
                        )}
                        <div
                          className={`max-w-[85%] rounded-2xl px-3.5 py-3 ${msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                          style={msg.role === 'user'
                            ? (userBubbleStyle ?? { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' })
                            : (settings ? assistantBubble : { background: 'hsl(var(--muted))' })}
                        >
                          {msg.role === 'assistant' ? (
                            <MarkdownRenderer variant="chat" className="text-inherit">
                              {msg.content}
                            </MarkdownRenderer>
                          ) : (
                            <p className="text-sm leading-[1.55] whitespace-pre-wrap break-words">{msg.content}</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Typing indicator */}
                  {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
                    <div className="flex gap-2">
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                        style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary)/0.1)' }}
                      >
                        <AgentAvatar />
                      </div>
                      <div
                        className="rounded-2xl rounded-tl-sm px-3.5 py-3"
                        style={settings ? assistantBubble : { background: 'hsl(var(--muted))' }}
                      >
                        <div className="flex gap-1">
                          {[0, 150, 300].map(d => (
                            <span
                              key={d}
                              className="h-1.5 w-1.5 rounded-full animate-bounce motion-reduce:animate-none"
                              style={{ background: settings ? `${settings.text_color}99` : 'hsl(var(--muted-foreground)/0.5)', animationDelay: `${d}ms` }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {error}
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Footer */}
                <div className="px-4 py-3.5 shrink-0" style={footerStyle}>
                  {isLocked ? (
                    <div className="flex flex-col items-center gap-1.5">
                      <p className="text-xs opacity-60" style={textStyle}>Esta conversa foi encerrada.</p>
                      <button
                        onClick={startNewConversation}
                        className="flex items-center gap-1 text-xs font-semibold hover:underline"
                        style={{ color: btnBg }}
                      >
                        <PlusCircle className="h-3.5 w-3.5" />
                        Nova conversa
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                        placeholder="Digite uma mensagem…"
                        disabled={isLoading || isLocked}
                        className="flex-1 text-sm placeholder:opacity-40 focus-visible:ring-1 h-10"
                        style={inputStyle}
                        maxLength={2000}
                      />
                      <Button
                        onClick={handleSend}
                        disabled={isLoading || isLocked || !input.trim()}
                        size="icon"
                        className="h-10 w-10 shrink-0"
                        style={sendBtnStyle}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <p className="text-[10px] opacity-25 text-center mt-2 tracking-wide" style={textStyle}>
                    Powered by AI
                  </p>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating trigger button ───────────────────────────────────────── */}
      <motion.button
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          trackChatWidgetEvent(next ? 'opened' : 'closed', { agent_id: agentId, source: 'widget' });          if (next && pageOwnerId) {
            trackFunnelStep(pageOwnerId, 'chat', 'chat_open').catch(() => {});
          }        }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.93 }}
        className="rounded-full shadow-xl flex items-center justify-center overflow-hidden transition-shadow hover:shadow-2xl"
        style={{ width: btnPx, height: btnPx, background: widgetBtnBg }}
        aria-label={agentInfo?.agent_name ?? 'Chat'}
        aria-expanded={isOpen}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.14 }}
            >
              <X style={{ width: 24 * iconScale, height: 24 * iconScale, color: widgetBtnText }} />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.14 }}
              className="flex items-center justify-center w-full h-full"
            >
              {agentInfo?.widget_icon_url && !widgetIconError ? (
                <img
                  src={agentInfo.widget_icon_url}
                  alt={agentInfo.agent_name}
                  style={{ width: 40 * iconScale, height: 40 * iconScale }}
                  className="object-cover rounded-full"
                  onError={() => setWidgetIconError(true)}
                />
              ) : agentInfo?.widget_icon && agentInfo.widget_icon !== '__chat__' ? (
                <span style={{ fontSize: 24 * iconScale }} className="leading-none select-none">{agentInfo.widget_icon}</span>
              ) : agentInfo?.widget_icon === '__chat__' ? (
                <MessageSquare style={{ width: 24 * iconScale, height: 24 * iconScale, color: widgetBtnText }} />
              ) : agentInfo?.avatar_url && !agentAvatarError ? (
                <img
                  src={agentInfo.avatar_url}
                  alt={agentInfo.agent_name}
                  style={{ width: 40 * iconScale, height: 40 * iconScale }}
                  className="object-cover rounded-full"
                  onError={() => setAgentAvatarError(true)}
                />
              ) : (
                <Bot style={{ width: 24 * iconScale, height: 24 * iconScale, color: widgetBtnText }} />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
